import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Investigation, InvestigationStatus } from '../types';
import { investigationService } from '../investigationService';
import { useEscapeKey } from '../../../../shared/useGlobalModalEscape';
import { useDialog } from '../../../../shared/DialogContext';

interface InvestigationWorkspaceModalProps {
  investigationId: string;
  onClose: () => void;
  onUpdated: () => void;
}

export const InvestigationWorkspaceModal: React.FC<InvestigationWorkspaceModalProps> = ({
  investigationId,
  onClose,
  onUpdated,
}) => {
  useEscapeKey(onClose);
  const { showError, showSuccess } = useDialog();

  const [inv, setInv] = useState<Investigation | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<
    'overview' | 'team' | 'evidence' | 'interviews' | 'methodologies' | 'findings' | 'recommendations' | 'review' | 'report'
  >('overview');

  // Form states
  const [actionReason, setActionReason] = useState<string>('');
  const [newTeamUser, setNewTeamUser] = useState<string>('');
  const [newTeamName, setNewTeamName] = useState<string>('');
  const [newTeamRole, setNewTeamRole] = useState<string>('INVESTIGATOR');
  const [newTeamDept, setNewTeamDept] = useState<string>('Flight Safety');

  const [evidenceTitle, setEvidenceTitle] = useState<string>('');
  const [evidenceKey, setEvidenceKey] = useState<string>('');
  const [evidenceRaw, setEvidenceRaw] = useState<string>('');

  const [witnessCode, setWitnessCode] = useState<string>('');
  const [witnessRole, setWitnessRole] = useState<string>('');
  const [interviewText, setInterviewText] = useState<string>('');

  // 5-Why form state
  const [whyProblem, setWhyProblem] = useState<string>('');
  const [why1, setWhy1] = useState<string>('');
  const [why2, setWhy2] = useState<string>('');
  const [why3, setWhy3] = useState<string>('');
  const [why4, setWhy4] = useState<string>('');
  const [why5, setWhy5] = useState<string>('');

  const [findingStatement, setFindingStatement] = useState<string>('');
  const [findingRef, setFindingRef] = useState<string>('');

  const [causeCategory, setCauseCategory] = useState<string>('TECHNICAL');
  const [causeDesc, setCauseDesc] = useState<string>('');
  const [causeSystemic, setCauseSystemic] = useState<boolean>(false);

  const [factorType, setFactorType] = useState<string>('HUMAN');
  const [factorDesc, setFactorDesc] = useState<string>('');

  const [recTitle, setRecTitle] = useState<string>('');
  const [recDesc, setRecDesc] = useState<string>('');
  const [recDept, setRecDept] = useState<string>('Flight Operations');
  const [recPrio, setRecPrio] = useState<string>('HIGH');
  const [recCategory, setRecCategory] = useState<string>('Flight Operations');
  const [recSeverity, setRecSeverity] = useState<string>('Major');
  const [recProbability, setRecProbability] = useState<string>('3');
  const [recIsSafetyRelevant, setRecIsSafetyRelevant] = useState<boolean>(true);
  const [recProjectLeader, setRecProjectLeader] = useState<string>('USR-PROJECT-LEAD');
  const [recResponsible, setRecResponsible] = useState<string>('USR-RESPONSIBLE');
  const [recDueDate, setRecDueDate] = useState<string>(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  );

  // Mitigation & verification states
  const [mitigatingRecId, setMitigatingRecId] = useState<string | null>(null);
  const [mitigationText, setMitigationText] = useState<string>('');
  const [verifyingRecId, setVerifyingRecId] = useState<string | null>(null);
  const [verificationComments, setVerificationComments] = useState<string>('');

  const [reviewVerdict, setReviewVerdict] = useState<'APPROVED' | 'REVISION_REQUESTED'>('APPROVED');
  const [reviewComments, setReviewComments] = useState<string>('');
  const [finalReportMarkdown, setFinalReportMarkdown] = useState<string>('');

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const data = await investigationService.getInvestigationById(investigationId);
      setInv(data);
      if (data.title && !whyProblem) {
        setWhyProblem(data.title);
      }
    } catch (err: any) {
      showError(err, 'Error Loading Investigation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [investigationId]);

  const handleTransition = async (status: InvestigationStatus) => {
    if (!inv) return;
    try {
      await investigationService.transitionStatus(inv.id, status, actionReason || `Status shifted to ${status}`);
      setActionReason('');
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      showError(err, 'Transition Failed');
    }
  };

  const handleAddTeamMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inv || !newTeamName.trim()) return;
    try {
      await investigationService.addTeamMember(
        inv.id,
        newTeamUser.trim() || `USER-${Date.now()}`,
        newTeamName.trim(),
        newTeamRole,
        newTeamDept
      );
      setNewTeamName('');
      setNewTeamUser('');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Add Team Member');
    }
  };

  const handleAddEvidence = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inv || !evidenceTitle.trim()) return;
    try {
      await investigationService.addEvidence(
        inv.id,
        evidenceTitle.trim(),
        evidenceKey.trim() || `vault/${evidenceTitle.trim()}`,
        'Digital inspection capture',
        evidenceRaw.trim() || undefined
      );
      setEvidenceTitle('');
      setEvidenceKey('');
      setEvidenceRaw('');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Evidence Upload Failed');
    }
  };

  const handleRecordInterview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inv || !witnessCode.trim() || !interviewText.trim()) return;
    try {
      await investigationService.recordInterview(
        inv.id,
        witnessCode.trim(),
        witnessRole.trim() || 'Crew Member',
        interviewText.trim(),
        true
      );
      setWitnessCode('');
      setWitnessRole('');
      setInterviewText('');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Interview Storage Failed');
    }
  };

  const handleSave5Why = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inv || !whyProblem.trim()) return;
    const whys = [
      { level: 1, why: why1 },
      { level: 2, why: why2 },
      { level: 3, why: why3 },
      { level: 4, why: why4 },
      { level: 5, why: why5 },
    ].filter(w => w.why.trim().length > 0);

    try {
      await investigationService.saveMethodologyAnalysis(inv.id, '5_WHY', {
        problem: whyProblem,
        levels: whys,
      }, why5 || why3);
      showSuccess('5-Why root cause analysis successfully saved to database!');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Save 5-Why');
    }
  };

  const handleAddFinding = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inv || !findingStatement.trim()) return;
    try {
      await investigationService.addFinding(inv.id, findingStatement.trim(), findingRef.trim() || undefined);
      setFindingStatement('');
      setFindingRef('');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Add Finding');
    }
  };

  const handleAddCause = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inv || !causeDesc.trim()) return;
    try {
      await investigationService.addRootCause(inv.id, causeCategory, causeDesc.trim(), 'PROBABLE', causeSystemic);
      setCauseDesc('');
      setCauseSystemic(false);
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Add Root Cause');
    }
  };

  const handleAddFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inv || !factorDesc.trim()) return;
    try {
      await investigationService.addContributingFactor(inv.id, factorType as any, factorDesc.trim(), 'MAJOR');
      setFactorDesc('');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Add Contributing Factor');
    }
  };

  const handleAddRecommendation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inv || !recTitle.trim()) return;
    try {
      await investigationService.addRecommendation(inv.id, {
        title: recTitle.trim(),
        description: recDesc.trim(),
        targetDepartment: recDept,
        priority: recPrio as any,
        category: recCategory,
        isSafetyRelevant: recIsSafetyRelevant,
        initialRiskSeverity: recSeverity,
        initialRiskProbability: recProbability,
        projectLeaderId: recProjectLeader,
        responsibleUserId: recResponsible,
        dueDate: recDueDate,
      });
      setRecTitle('');
      setRecDesc('');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Add Safety Recommendation');
    }
  };

  const handleRecordMitigation = async () => {
    if (!mitigatingRecId || !mitigationText.trim()) return;
    try {
      await investigationService.recordMitigationAction(mitigatingRecId, mitigationText.trim());
      setMitigatingRecId(null);
      setMitigationText('');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Failed to Record Mitigation Action');
    }
  };

  const handleVerifyClosure = async (isApproved: boolean) => {
    if (!verifyingRecId) return;
    try {
      await investigationService.verifyClosureRecommendation(
        verifyingRecId,
        isApproved,
        verificationComments.trim() || undefined
      );
      setVerifyingRecId(null);
      setVerificationComments('');
      await fetchDetail();
    } catch (err: any) {
      showError(err, 'Verification Closure Failed');
    }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inv || !reviewComments.trim()) return;
    try {
      await investigationService.submitReview(inv.id, reviewVerdict, reviewComments.trim());
      setReviewComments('');
      await fetchDetail();
      onUpdated();
    } catch (err: any) {
      showError(err, 'Review Signoff Failed');
    }
  };

  const loadFinalReport = async () => {
    if (!inv) return;
    try {
      const res = await investigationService.getFinalReportMarkdown(inv.id);
      setFinalReportMarkdown(res.markdown);
    } catch (err: any) {
      setFinalReportMarkdown(`Error loading report: ${err.message}`);
    }
  };

  useEffect(() => {
    if (activeTab === 'report') {
      loadFinalReport();
    }
  }, [activeTab]);

  if (loading || !inv) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: '#fff', padding: '30px', borderRadius: '8px' }}>Loading investigation workspace...</div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop">
      <div className="modal-card max-w-5xl" style={{ height: '92vh' }}>
        {/* Workspace Top Header */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#0f172a', color: '#ffffff', borderRadius: '10px 10px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#c084fc' }}>{inv.investigationNumber}</span>
              <span style={{ fontSize: '11px', background: '#334155', color: '#f1f5f9', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                {inv.status}
              </span>
              <span style={{ fontSize: '11px', background: '#475569', color: '#f1f5f9', padding: '2px 8px', borderRadius: '4px' }}>
                Type: {inv.type}
              </span>
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#f8fafc', marginTop: '4px' }}>{inv.title}</div>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            style={{ color: '#94a3b8' }}
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Strip */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', padding: '0 24px', gap: '4px', overflowX: 'auto' }}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'team', label: `Team (${inv.team?.length || 0})` },
            { id: 'evidence', label: `Evidence Vault (${inv.evidence?.length || 0})` },
            { id: 'interviews', label: `Interviews (${inv.interviews?.length || 0})` },
            { id: 'methodologies', label: 'Methodologies Workbench' },
            { id: 'findings', label: `Findings & Causes (${(inv.findings?.length || 0) + (inv.rootCauses?.length || 0)})` },
            { id: 'recommendations', label: `Recommendations (${inv.recommendations?.length || 0})` },
            { id: 'review', label: 'Safety Manager Review' },
            { id: 'report', label: 'Final Report' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #9333ea' : '2px solid transparent',
                color: activeTab === tab.id ? '#9333ea' : '#64748b',
                padding: '12px 12px',
                fontSize: '13px',
                fontWeight: activeTab === tab.id ? 600 : 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Body */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1, fontSize: '13px', color: '#334155' }}>
          {activeTab === 'overview' && (
            <div>
              {/* Lifecycle Advance Controls */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', background: '#f8fafc', padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: '#475569', marginRight: '6px' }}>Advance Stage:</span>
                {inv.status === 'INITIATED' && (
                  <button onClick={() => handleTransition('EVIDENCE_COLLECTION')} style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
                    Proceed to Evidence Collection →
                  </button>
                )}
                {inv.status === 'EVIDENCE_COLLECTION' && (
                  <button onClick={() => handleTransition('ROOT_CAUSE_ANALYSIS')} style={{ background: '#7c3aed', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
                    Proceed to Root Cause Analysis →
                  </button>
                )}
                {inv.status === 'ROOT_CAUSE_ANALYSIS' && (
                  <button onClick={() => handleTransition('DRAFT_REPORT')} style={{ background: '#ea580c', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
                    Prepare Draft Report →
                  </button>
                )}
                {inv.status === 'DRAFT_REPORT' && (
                  <button onClick={() => handleTransition('PENDING_REVIEW')} style={{ background: '#b91c1c', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
                    Submit for Safety Manager Review ✉
                  </button>
                )}
                {inv.status === 'APPROVED' && (
                  <button onClick={() => handleTransition('CLOSED')} style={{ background: '#15803d', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
                    Close Investigation Case 🔒
                  </button>
                )}
              </div>

              {/* Overview Details Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '20px' }}>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>Lead Investigator</div>
                  <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{inv.leadInvestigatorName || 'Unassigned'}</div>
                </div>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>Target Completion</div>
                  <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{new Date(inv.targetCompletionDate).toLocaleDateString()}</div>
                </div>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>Priority Level</div>
                  <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{inv.priority}</div>
                </div>
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase' }}>Occurrence Ref</div>
                  <div style={{ fontWeight: 600, color: '#0284c7', marginTop: '2px' }}>{inv.occurrenceNumber || 'Standalone'}</div>
                </div>
              </div>

              {/* Case Summary */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '8px' }}>Executive Summary & Initial Scope</div>
                <p style={{ margin: 0, lineHeight: 1.6, color: '#334155' }}>{inv.summary || 'No initial summary recorded.'}</p>
              </div>
            </div>
          )}

          {activeTab === 'team' && (
            <div>
              <h4 style={{ margin: '0 0 14px 0', color: '#0f172a' }}>Investigation Team Roster</h4>
              <form onSubmit={handleAddTeamMember} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 180px 180px auto', gap: '8px', marginBottom: '16px' }}>
                <input
                  type="text"
                  placeholder="User ID"
                  value={newTeamUser}
                  onChange={(e) => setNewTeamUser(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
                <input
                  type="text"
                  placeholder="Full Name"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  required
                />
                <select value={newTeamRole} onChange={(e) => setNewTeamRole(e.target.value)} style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                  <option value="INVESTIGATOR">Investigator</option>
                  <option value="TECHNICAL_SPECIALIST">Technical Specialist</option>
                  <option value="SAFETY_MANAGER_REVIEWER">Safety Reviewer</option>
                  <option value="LEAD_INVESTIGATOR">Lead Investigator</option>
                </select>
                <input
                  type="text"
                  placeholder="Department"
                  value={newTeamDept}
                  onChange={(e) => setNewTeamDept(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
                <button type="submit" style={{ background: '#9333ea', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>
                  Add Member
                </button>
              </form>

              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '8px 12px' }}>Name</th>
                    <th style={{ padding: '8px 12px' }}>Role</th>
                    <th style={{ padding: '8px 12px' }}>Department</th>
                    <th style={{ padding: '8px 12px' }}>Assigned Date</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.team?.map((m) => (
                    <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 600 }}>{m.userName}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ background: '#f3e8ff', color: '#7e22ce', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                          {m.teamRole}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', color: '#64748b' }}>{m.department}</td>
                      <td style={{ padding: '8px 12px', color: '#64748b' }}>{new Date(m.assignedAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'evidence' && (
            <div>
              <h4 style={{ margin: '0 0 14px 0', color: '#0f172a' }}>Digital Evidence Vault & Chain of Custody</h4>
              <form onSubmit={handleAddEvidence} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', marginBottom: '16px' }}>
                <input
                  type="text"
                  placeholder="Evidence title (e.g. Flight Recorder Data Dump)"
                  value={evidenceTitle}
                  onChange={(e) => setEvidenceTitle(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  required
                />
                <input
                  type="text"
                  placeholder="Storage key (optional)"
                  value={evidenceKey}
                  onChange={(e) => setEvidenceKey(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
                <input
                  type="text"
                  placeholder="Raw content string for cryptographic hash"
                  value={evidenceRaw}
                  onChange={(e) => setEvidenceRaw(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
                <button type="submit" style={{ background: '#9333ea', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>
                  Vault Evidence
                </button>
              </form>

              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '8px 12px' }}>Item #</th>
                    <th style={{ padding: '8px 12px' }}>Title</th>
                    <th style={{ padding: '8px 12px' }}>SHA-256 Hash Seal</th>
                    <th style={{ padding: '8px 12px' }}>Integrity</th>
                    <th style={{ padding: '8px 12px' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.evidence?.map((ev) => (
                    <tr key={ev.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 600 }}>{ev.itemNumber}</td>
                      <td style={{ padding: '8px 12px' }}>{ev.title}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '11px', color: '#475569' }}>
                        {ev.fileHash.slice(0, 16)}...{ev.fileHash.slice(-8)}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                          {ev.tamperStatus}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', color: '#64748b' }}>{new Date(ev.collectionDate).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'interviews' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <h4 style={{ margin: 0, color: '#0f172a' }}>Just Culture Witness Interview Vault</h4>
                <span style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 700 }}>
                  NON-PUNITIVE PROTECTION
                </span>
              </div>
              <form onSubmit={handleRecordInterview} style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px', background: '#f8fafc', padding: '14px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <input
                    type="text"
                    placeholder="Witness Code (e.g. WITNESS-CPT-01)"
                    value={witnessCode}
                    onChange={(e) => setWitnessCode(e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Witness Role (e.g. Captain / First Officer)"
                    value={witnessRole}
                    onChange={(e) => setWitnessRole(e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  />
                </div>
                <textarea
                  rows={3}
                  placeholder="Confidential interview testimony..."
                  value={interviewText}
                  onChange={(e) => setInterviewText(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  required
                />
                <button type="submit" style={{ background: '#9333ea', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', alignSelf: 'flex-start', fontWeight: 600 }}>
                  Encrypt & Vault Statement
                </button>
              </form>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {inv.interviews?.map((int) => (
                  <div key={int.id} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 600, color: '#9333ea' }}>{int.witnessCode} ({int.witnessRole})</span>
                      <span style={{ fontSize: '11px', color: '#64748b' }}>Date: {new Date(int.interviewDate).toLocaleDateString()}</span>
                    </div>
                    <div style={{ fontFamily: 'monospace', background: '#f8fafc', padding: '8px', borderRadius: '4px', fontSize: '11px', color: '#475569' }}>
                      [PROTECTED TRANSCRIPT]: {atob(int.transcriptEncrypted)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'methodologies' && (
            <div>
              <h4 style={{ margin: '0 0 14px 0', color: '#0f172a' }}>Interactive 5-Why Root Cause Workbench</h4>
              <form onSubmit={handleSave5Why} style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: '#f8fafc', padding: '16px', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Problem Statement</label>
                  <input
                    type="text"
                    value={whyProblem}
                    onChange={(e) => setWhyProblem(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, fontSize: '12px', color: '#475569' }}>Why 1 (Immediate symptom)</label>
                  <input type="text" value={why1} onChange={(e) => setWhy1(e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, fontSize: '12px', color: '#475569' }}>Why 2</label>
                  <input type="text" value={why2} onChange={(e) => setWhy2(e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, fontSize: '12px', color: '#475569' }}>Why 3 (Mechanism)</label>
                  <input type="text" value={why3} onChange={(e) => setWhy3(e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, fontSize: '12px', color: '#475569' }}>Why 4</label>
                  <input type="text" value={why4} onChange={(e) => setWhy4(e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', color: '#9333ea' }}>Why 5 (Root Cause)</label>
                  <input type="text" value={why5} onChange={(e) => setWhy5(e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                </div>
                <button type="submit" style={{ background: '#9333ea', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', alignSelf: 'flex-start', fontWeight: 600 }}>
                  Save 5-Why Analysis
                </button>
              </form>
            </div>
          )}

          {activeTab === 'findings' && (
            <div>
              <h4 style={{ margin: '0 0 14px 0', color: '#0f172a' }}>Findings, Root Causes & Contributing Factors</h4>
              {/* Finding Form */}
              <form onSubmit={handleAddFinding} style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                <input
                  type="text"
                  placeholder="Document factual finding statement..."
                  value={findingStatement}
                  onChange={(e) => setFindingStatement(e.target.value)}
                  style={{ flex: 1, padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  required
                />
                <input
                  type="text"
                  placeholder="Evidence Ref (e.g. EVD-001)"
                  value={findingRef}
                  onChange={(e) => setFindingRef(e.target.value)}
                  style={{ width: '160px', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
                <button type="submit" style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>
                  + Finding
                </button>
              </form>

              {/* Root Cause Form */}
              <form onSubmit={handleAddCause} style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto auto', gap: '8px', marginBottom: '20px' }}>
                <input
                  type="text"
                  placeholder="Category (e.g. PROCEDURE)"
                  value={causeCategory}
                  onChange={(e) => setCauseCategory(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  required
                />
                <input
                  type="text"
                  placeholder="Root cause description..."
                  value={causeDesc}
                  onChange={(e) => setCauseDesc(e.target.value)}
                  style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                  required
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                  <input type="checkbox" checked={causeSystemic} onChange={(e) => setCauseSystemic(e.target.checked)} />
                  Systemic
                </label>
                <button type="submit" style={{ background: '#7c3aed', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>
                  + Root Cause
                </button>
              </form>

              {/* Lists */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <h5 style={{ margin: '0 0 8px 0', color: '#475569' }}>Factual Findings</h5>
                  {inv.findings?.map(f => (
                    <div key={f.id} style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '4px', marginBottom: '6px', border: '1px solid #e2e8f0' }}>
                      <strong>{f.findingNumber}:</strong> {f.statement}
                    </div>
                  ))}
                </div>
                <div>
                  <h5 style={{ margin: '0 0 8px 0', color: '#475569' }}>Root Causes</h5>
                  {inv.rootCauses?.map(rc => (
                    <div key={rc.id} style={{ background: '#faf5ff', padding: '8px 10px', borderRadius: '4px', marginBottom: '6px', border: '1px solid #e9d5ff' }}>
                      <span style={{ fontWeight: 600, color: '#6b21a8' }}>[{rc.causeCategory}]</span> {rc.description}
                      {rc.systemicIndicator && (
                        <span style={{ marginLeft: '6px', background: '#fee2e2', color: '#b91c1c', padding: '1px 4px', borderRadius: '3px', fontSize: '10px' }}>
                          SYSTEMIC
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'recommendations' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <h4 style={{ margin: 0, color: '#0f172a' }}>Safety Recommendations & Risk Rating</h4>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const data = await investigationService.exportRecommendations({ safety_relevant_only: true });
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `recommendations-${inv.investigationNumber}.json`;
                      a.click();
                    } catch (err: any) {
                      showError(err, 'Export Failed');
                    }
                  }}
                  style={{ background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                >
                  Export Recommendations
                </button>
              </div>

              {/* Formulate Safety Recommendation Form */}
              <form onSubmit={handleAddRecommendation} style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Recommendation Title *</label>
                    <input
                      type="text"
                      placeholder="e.g. Mandate descent avoidance buffer for convective SIGMETs"
                      value={recTitle}
                      onChange={(e) => setRecTitle(e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Category *</label>
                    <select
                      value={recCategory}
                      onChange={(e) => setRecCategory(e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                    >
                      <option value="Flight Operations">Flight Operations</option>
                      <option value="Ground Operations">Ground Operations</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Security">Security</option>
                      <option value="Training">Training</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Target Department</label>
                    <input
                      type="text"
                      value={recDept}
                      onChange={(e) => setRecDept(e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Action Details & Justification *</label>
                  <textarea
                    rows={2}
                    placeholder="Describe specific preventive or corrective actions expected..."
                    value={recDesc}
                    onChange={(e) => setRecDesc(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Initial Severity *</label>
                    <select
                      value={recSeverity}
                      onChange={(e) => setRecSeverity(e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                    >
                      <option value="Catastrophic">Catastrophic (5)</option>
                      <option value="Hazardous">Hazardous (4)</option>
                      <option value="Major">Major (3)</option>
                      <option value="Minor">Minor (2)</option>
                      <option value="Negligible">Negligible (1)</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Initial Probability *</label>
                    <select
                      value={recProbability}
                      onChange={(e) => setRecProbability(e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                    >
                      <option value="5">Frequent (5 / A)</option>
                      <option value="4">Probable (4 / B)</option>
                      <option value="3">Remote (3 / C)</option>
                      <option value="2">Extremely Remote (2 / D)</option>
                      <option value="1">Extremely Improbable (1 / E)</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Target Due Date *</label>
                    <input
                      type="date"
                      value={recDueDate}
                      onChange={(e) => setRecDueDate(e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                      required
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', height: '36px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: '#1e293b', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={recIsSafetyRelevant}
                        onChange={(e) => setRecIsSafetyRelevant(e.target.checked)}
                      />
                      Safety-Relevant
                    </label>
                  </div>

                  <button
                    type="submit"
                    style={{ background: '#9333ea', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Formulate
                  </button>
                </div>
              </form>

              {/* Recommendations Table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '8px 10px' }}>Rec #</th>
                    <th style={{ padding: '8px 10px' }}>Title & Category</th>
                    <th style={{ padding: '8px 10px' }}>Risk Matrix</th>
                    <th style={{ padding: '8px 10px' }}>Safety Relevance</th>
                    <th style={{ padding: '8px 10px' }}>Due Date</th>
                    <th style={{ padding: '8px 10px' }}>Status</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.recommendations?.map((r) => {
                    const isRed = r.initialRiskColor === 'RED';
                    const isYellow = r.initialRiskColor === 'YELLOW';
                    const riskBg = isRed ? '#fee2e2' : isYellow ? '#fef9c3' : '#dcfce7';
                    const riskTextColor = isRed ? '#b91c1c' : isYellow ? '#854d0e' : '#15803d';

                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 10px', fontWeight: 600 }}>{r.recommendationNumber}</td>
                        <td style={{ padding: '10px 10px' }}>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>{r.title}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>{r.category || r.targetDepartment}</div>
                        </td>
                        <td style={{ padding: '10px 10px' }}>
                          <span
                            style={{
                              background: riskBg,
                              color: riskTextColor,
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 700,
                            }}
                          >
                            {r.initialRiskIndex || 'N/A'} {r.initialRiskSeverity ? `(${r.initialRiskSeverity})` : ''}
                          </span>
                        </td>
                        <td style={{ padding: '10px 10px' }}>
                          <span
                            style={{
                              background: r.isSafetyRelevant ? '#e0e7ff' : '#f1f5f9',
                              color: r.isSafetyRelevant ? '#3730a3' : '#64748b',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                            }}
                          >
                            {r.isSafetyRelevant ? 'Safety Relevant' : 'Non-Safety'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 10px', fontSize: '12px' }}>{r.dueDate || 'N/A'}</td>
                        <td style={{ padding: '10px 10px' }}>
                          <span
                            style={{
                              background: r.status === 'CLOSED' ? '#dcfce7' : r.status === 'PENDING_VERIFICATION' ? '#fef9c3' : '#e2e8f0',
                              color: r.status === 'CLOSED' ? '#15803d' : r.status === 'PENDING_VERIFICATION' ? '#854d0e' : '#334155',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                            }}
                          >
                            {r.status}
                          </span>
                          {r.mitigations && r.mitigations.length > 0 && (
                            <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                              {r.mitigations.length} mitigation(s)
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '10px 10px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            {r.status !== 'CLOSED' && (
                              <button
                                type="button"
                                onClick={() => {
                                  setMitigatingRecId(r.id);
                                  setMitigationText('');
                                }}
                                style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}
                              >
                                Mitigate
                              </button>
                            )}
                            {r.status === 'PENDING_VERIFICATION' && (
                              <button
                                type="button"
                                onClick={() => {
                                  setVerifyingRecId(r.id);
                                  setVerificationComments('');
                                }}
                                style={{ background: '#10b981', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}
                              >
                                Verify & Close
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Modal for Recording Mitigation Action (ROLE_RESPONSIBLE) */}
              {mitigatingRecId && (
                <div className="modal-backdrop" style={{ zIndex: 9999 }}>
                  <div className="modal-card" style={{ maxWidth: '500px' }}>
                    <div className="modal-header">
                      <h3>Record Mitigation Action</h3>
                      <button type="button" onClick={() => setMitigatingRecId(null)} className="modal-close-btn">
                        <X size={18} />
                      </button>
                    </div>
                    <div className="modal-body">
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                        Mitigation Action Taken (ROLE_RESPONSIBLE) *
                      </label>
                      <textarea
                        rows={4}
                        placeholder="Detail the operational, technical, or procedural corrective actions executed in the field..."
                        value={mitigationText}
                        onChange={(e) => setMitigationText(e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '6px' }}
                      />
                    </div>
                    <div className="modal-footer">
                      <button type="button" onClick={() => setMitigatingRecId(null)} className="btn-secondary">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleRecordMitigation}
                        disabled={!mitigationText.trim()}
                        className="btn-primary"
                      >
                        Submit Mitigation
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal for Project Leader Verification Closure (ROLE_PROJECT_LEADER) */}
              {verifyingRecId && (
                <div className="modal-backdrop" style={{ zIndex: 9999 }}>
                  <div className="modal-card" style={{ maxWidth: '500px' }}>
                    <div className="modal-header">
                      <h3>Verify & Close Recommendation</h3>
                      <button type="button" onClick={() => setVerifyingRecId(null)} className="modal-close-btn">
                        <X size={18} />
                      </button>
                    </div>
                    <div className="modal-body">
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                        Project Leader Verification Comments (Optional)
                      </label>
                      <textarea
                        rows={3}
                        placeholder="Document verification evidence, effectiveness checks, and sign-off justification..."
                        value={verificationComments}
                        onChange={(e) => setVerificationComments(e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '6px' }}
                      />
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        onClick={() => handleVerifyClosure(false)}
                        className="btn-secondary"
                        style={{ color: '#b91c1c' }}
                      >
                        Reject Verification
                      </button>
                      <button
                        type="button"
                        onClick={() => handleVerifyClosure(true)}
                        className="btn-primary"
                        style={{ background: '#10b981' }}
                      >
                        Approve & Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'review' && (
            <div>
              <h4 style={{ margin: '0 0 14px 0', color: '#0f172a' }}>Safety Manager Review & Sign-Off Gate</h4>
              <form onSubmit={handleSubmitReview} style={{ background: '#f8fafc', padding: '16px', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: '#15803d', cursor: 'pointer' }}>
                    <input type="radio" name="verdict" checked={reviewVerdict === 'APPROVED'} onChange={() => setReviewVerdict('APPROVED')} />
                    APPROVE INVESTIGATION & RECOMMENDATIONS
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: '#b91c1c', cursor: 'pointer' }}>
                    <input type="radio" name="verdict" checked={reviewVerdict === 'REVISION_REQUESTED'} onChange={() => setReviewVerdict('REVISION_REQUESTED')} />
                    REQUEST REVISIONS
                  </label>
                </div>
                <textarea
                  rows={3}
                  placeholder="Review comments and feedback..."
                  value={reviewComments}
                  onChange={(e) => setReviewComments(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '4px', border: '1px solid #cbd5e1', marginBottom: '10px' }}
                  required
                />
                <button type="submit" style={{ background: reviewVerdict === 'APPROVED' ? '#15803d' : '#b91c1c', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>
                  Submit Official Decision
                </button>
              </form>

              {inv.reviews && inv.reviews.length > 0 && (
                <div>
                  <h5 style={{ margin: '0 0 8px 0', color: '#475569' }}>Review History</h5>
                  {inv.reviews.map(rev => (
                    <div key={rev.id} style={{ background: '#ffffff', padding: '10px', borderRadius: '4px', border: '1px solid #e2e8f0', marginBottom: '6px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 600 }}>{rev.reviewerName}</span>
                        <span style={{ fontWeight: 700, color: rev.verdict === 'APPROVED' ? '#15803d' : '#b91c1c' }}>{rev.verdict}</span>
                      </div>
                      <p style={{ margin: 0, color: '#334155' }}>{rev.comments}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'report' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, color: '#0f172a' }}>Compiled Executive Safety Investigation Report</h4>
                <button onClick={loadFinalReport} style={{ background: '#9333ea', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                  Refresh Report
                </button>
              </div>
              <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: '20px', borderRadius: '6px', fontSize: '12px', lineHeight: 1.6, overflowX: 'auto', maxHeight: '450px' }}>
                {finalReportMarkdown}
              </pre>
            </div>
          )}
        </div>

        {/* Workspace Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'flex-end', borderRadius: '0 0 10px 10px' }}>
          <button onClick={onClose} style={{ background: '#e2e8f0', color: '#334155', border: 'none', padding: '6px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
            Close Workspace
          </button>
        </div>
      </div>
    </div>
  );
};
